import React from "react";
import { UserPaymentTrancheApi } from "api";
import { PaziaMenu } from "components/atoms";
import { REGISTRATION_TRANCHE_PAYMENT_STATUSES, REGISTRATION_TRANCHE_PAYMENT_STATUSES_LABELS } from "consts";
import { daysToDate, formatDate, toMoneyFormat } from "utils";
import { MdEdit } from "react-icons/md";
import { IoLink } from "react-icons/io5";
import { PaziaBadge } from "components/atoms";
import { ThemeColors } from "consts/theme";
import { IoTimeSharp } from "react-icons/io5";

interface StudentPaymentsTrancheRowProps {
  tranche: UserPaymentTrancheApi;
  onPostponePayment: (courseTrancheId: number) => void;
  setEditStatusPayment: (tranche: UserPaymentTrancheApi) => void;
  setEditPricePayment: (tranche: UserPaymentTrancheApi) => void;
  onGeneratePaymentLink: (tranche: UserPaymentTrancheApi) => void;
  onEditTranche: (tranche: UserPaymentTrancheApi) => void;
}
export const getTrancheActions = ({
  tranche,
  onPostponePayment,
  setEditStatusPayment,
  setEditPricePayment,
  onGeneratePaymentLink,
  onEditTranche,
}: StudentPaymentsTrancheRowProps) => {
  const actions = [
    { label: "Edytuj transzę", icon: <MdEdit />, onClick: () => onEditTranche(tranche) },
    { label: "Zmień status", icon: <MdEdit />, onClick: () => setEditStatusPayment(tranche) },
  ];

  // Add generate payment link action for unpaid tranches
  if (
    tranche.status === REGISTRATION_TRANCHE_PAYMENT_STATUSES.NOT_PAID ||
    tranche.status === REGISTRATION_TRANCHE_PAYMENT_STATUSES.PENDING_PAYMENT
  ) {
    actions.push({
      label: "Generuj link płatności",
      icon: <IoLink size={16} />,
      onClick: () => onGeneratePaymentLink(tranche),
    });
  }

  if (
    tranche.status !== REGISTRATION_TRANCHE_PAYMENT_STATUSES.PAID_BY_USER &&
    tranche.status !== REGISTRATION_TRANCHE_PAYMENT_STATUSES.PENDING_PAYMENT
  ) {
    actions.push({ label: "Zmień cenę", icon: <MdEdit />, onClick: () => setEditPricePayment(tranche) });
  }
  if (tranche.status === REGISTRATION_TRANCHE_PAYMENT_STATUSES.NOT_PAID) {
    actions.push({
      label: "Odrocz płatność",
      icon: <IoTimeSharp size={16} />,
      onClick: () => onPostponePayment(tranche.courseTrancheId),
    });
  }
  return actions;
};

export const PAYMENT_STATUSES_BADGE_LABELS_COLORS: Record<number, ThemeColors> = {
  [REGISTRATION_TRANCHE_PAYMENT_STATUSES.NOT_PAID]: "amber",
  [REGISTRATION_TRANCHE_PAYMENT_STATUSES.PENDING_PAYMENT]: "primary",
  [REGISTRATION_TRANCHE_PAYMENT_STATUSES.PAID_BY_USER]: "emerald",
  [REGISTRATION_TRANCHE_PAYMENT_STATUSES.SKIPPED_BY_ADMIN]: "slate",
};

export const StudentPaymentsTrancheRow = (props: StudentPaymentsTrancheRowProps) => {
  const { tranche } = props;
  const actions = getTrancheActions(props);
  return (
    <tr
      key={tranche.courseTrancheId}
      className="group border-b border-slate-200 transition-colors duration-200 hover:bg-slate-50 md:table-row dark:border-slate-700 dark:hover:bg-slate-800/50"
    >
      <TableDefinition className="md:w-16">
        <div className="flex items-center">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-400">
            {tranche.courseTrancheId}
          </span>
        </div>
      </TableDefinition>

      <TableDefinition className="md:min-w-32">
        <div className="font-medium text-slate-900 dark:text-slate-100">{tranche.name}</div>
      </TableDefinition>

      <TableDefinition className="md:min-w-36">
        <div className="space-y-1">
          <div className="font-medium text-slate-900 dark:text-slate-100">{formatDate(new Date(tranche.dueDate))}</div>
          {tranche.status !== REGISTRATION_TRANCHE_PAYMENT_STATUSES.PAID_BY_USER &&
            (() => {
              const days = daysToDate(new Date(tranche.dueDate));
              const isOverdue = days < 0;
              return (
                <div
                  className={`text-sm font-medium ${isOverdue ? "text-red-600 dark:text-red-400" : "text-primary-600 dark:text-primary-400"}`}
                >
                  {isOverdue ? `${Math.abs(days)} dni po terminie` : `za ${days} dni`}
                </div>
              );
            })()}
        </div>
      </TableDefinition>

      <TableDefinition className="md:min-w-28">
        <div className="space-y-1">
          {tranche.amount !== tranche.discountedAmount ? (
            <>
              <div className="text-sm text-slate-500 line-through dark:text-slate-400">
                {toMoneyFormat(tranche.amount, true)}
              </div>
              <div className="font-semibold text-slate-900 dark:text-slate-100">
                {toMoneyFormat(tranche.discountedAmount, true)}
              </div>
            </>
          ) : (
            <div className="font-semibold text-slate-900 dark:text-slate-100">
              {toMoneyFormat(tranche.amount, true)}
            </div>
          )}
        </div>
      </TableDefinition>

      <TableDefinition className="md:min-w-32">
        <PaziaBadge color={PAYMENT_STATUSES_BADGE_LABELS_COLORS[tranche.status]} size="sm">
          {REGISTRATION_TRANCHE_PAYMENT_STATUSES_LABELS[tranche.status]}
        </PaziaBadge>
      </TableDefinition>

      <TableDefinition className="md:w-20">
        <div className="flex justify-end">
          <PaziaMenu title="Zarządzaj" actions={actions} />
        </div>
      </TableDefinition>
    </tr>
  );
};

export const TableHeader = ({ children }: { children: React.ReactNode }) => {
  return (
    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
      {children}
    </th>
  );
};

export const TableDefinition = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  return <td className={`px-3 py-4 align-top text-sm md:table-cell ${className}`}>{children}</td>;
};
